import { createSelector } from 'reselect';
import invert from 'lodash/invert';

import { renderDate } from '../../shared/dateHelpers';

import { YES_FLAG } from '../constants';
import { FUNCTIONAL_CURRENCY } from '@common/constants/sharedConstants';

import {
  checkAccessBasedOnAssignmentPermissions, getPermissionsFromTree,
} from '../../shared/authorization';
import { byField } from '../../utils/sorting';
import { currentClientSelector } from '../../userData/store/selectors';
import { routesPermissions } from '../../routesPermissions';
import { allRoutesArray } from '../../routes';

export const appDataSelector = ({ shared }) => shared.app;

export const environmentSelector = createSelector(
  appDataSelector,
  app => app.environment
);


const companyCodeToOption = companyCode => ({
  label: companyCode.fullDesc,
  value: companyCode.cc,
  enabledFlag: companyCode.enabledFlag,
  connection: companyCode.connection,
  legalEntity: companyCode.legalEntity,
  countryCode: companyCode.countryCode,
  subledgerFlag: companyCode.subledgerFlag === YES_FLAG,
  statCalc: companyCode.statCalc,
});

export const companyCodesSelector = state => state.shared.companyCodes;

export const companyCodesOptions = createSelector(
  companyCodesSelector,
  companyCodes => companyCodes.map(companyCodeToOption).sort(byField('label'))
);

export const companyCodesSetSelector = createSelector(
  companyCodesSelector,
  companyCodes => new Set(companyCodes.map(companyCode => companyCode.cc))
);

export const taxYearsOptionsSelector = createSelector(
  state => state.shared.taxYears,
  taxYears => taxYears.map(({ name, value }) => ({ label: name, value }))
);

export const taxYearsWithAllOptionsSelector = createSelector(
  state => state.shared.taxYears,
  taxYears => [{ value: '-1', label: 'All' }].concat(taxYears.map(({ name, value }) => ({
    label: name,
    value,
  })))
);

export const taxYearOrAllSelector = createSelector(
  ({ shared }) => shared.taxYear,
  taxYearsWithAllOptionsSelector,
  (taxYear, taxYears) => {
    if (taxYear && taxYear.label && taxYear.value) {
      return taxYear;
    }

    return taxYears.find(({ value }) => value === '-1');
  }
);

export const periodsSelector = createSelector(
  ({ shared }) => shared.periods,
  periods => periods.map(({ name, taxYear }) => ({
    name,
    value: name,
    taxYear,
  }))
);

export const subledgerFlagCompanyCodesSelector = createSelector(
  state => state.shared.companyCodes,
  companyCodes => companyCodes
    .filter(companyCode => companyCode.subledgerFlag === YES_FLAG)
    .map(companyCodeToOption)
    .sort(byField('label'))
);

export const connectionsOptionsSelector = createSelector(
  state => state.shared.connections,
  connections => connections.map(({ connName }) => ({
    label: connName,
    value: connName,
  }))
);

export const connectionsByTypeOptionsSelector = createSelector(
  state => state.shared.connectionsByType,
  connectionsByType => connectionsByType.map(({ connName }) => ({
    label: connName,
    value: connName,
  }))
);

export const connectionSelector = state => state.shared.connection;
export const connectionByTypeSelector = state => state.shared.connectionByType;

export const currentPeriodSelector = ({ shared }) => shared.currentPeriod;

export const periodOrCurrentSelector = ({ shared }) => shared.period || shared.currentPeriod;

export const isInCurrentPeriodSelector = ({ shared }) => !shared.period
  || shared.period.name === shared.currentPeriod.name;

export const calculationsOptionsSelector = createSelector(
  state => state.shared.calculationIdList,
  calculationIdList => calculationIdList.map(({ calcJobId, dateCreated }) => ({
    label: `${calcJobId} - ${renderDate(dateCreated)}`,
    value: calcJobId,
    dateCreated,
  }))
);

export const calculationSelector = state => state.shared.calculationIdSelected;

export const companyCodesByAssignmentPermissionSelector = createSelector(
  subledgerFlagCompanyCodesSelector,
  ({ user }) => user.user,
  ({ assignmentToCheck }) => assignmentToCheck,
  (companyCodes, user, assignmentToCheck) => companyCodes.filter(companyCode => (
    checkAccessBasedOnAssignmentPermissions({
      user,
      companyCode: companyCode.value,
      withAssignments: [assignmentToCheck],
    }))
  )
);

export const userRoleSelector = ({ user }) => user.user ? user.user.role : null;
export const userSelector = ({ user }) => user.user;
export const currentSsoSelector = ({ user }) => user.user.sso;

const activeClientSelectorFactory = field => createSelector(
  ({ user }) => user.clients,
  ({ user }) => user.user.lastClientId,

  (clients, lastClientId) => {
    const activeClient = clients.find(client => client.clientId === lastClientId);
    const value = activeClient && activeClient[field];

    return value ? value : '';
  }
);

export const clientNameSelector = activeClientSelectorFactory('shortName');

export const clientLegalEntitySourceSelector = activeClientSelectorFactory('legalEntitySource');

export const istClientIdSelector = activeClientSelectorFactory('istClientId');

export const environmentSuffixSelector = createSelector(
  environmentSelector,
  clientNameSelector,
  (environment, clientName) => {
    if (environment) {
      return `_${clientName}_(${environment})`;
    }
    return clientName ? `_${clientName}` : '';
  }
);


export const reportingCurrencyCodesSelector = createSelector(
  ({ shared }) => shared.reportingCurrencyCodes,
  currencyCodes => currencyCodes.map(({ value }) => ({ value, label: value })));

export const extraReportingCurrencyCodesSelector = extraCurrencies => createSelector(
  ({ shared }) => shared.reportingCurrencyCodes,
  currencyCodes => {
    const mappedExtraCurrencies = extraCurrencies.map(currency => ({ value: currency }));
    const newCurrencyCodes = [
      ...currencyCodes.slice(0, 1),
      ...mappedExtraCurrencies,
      ...currencyCodes.slice(1),
    ];
    return newCurrencyCodes.map(({ value }) => ({ value, label: value }));
  }
);

export const reportingCurrencyCodeSelector = ({ shared }) => shared.reportingCurrencyCode;
export const isFunctionalCurrencySelectedSelector = createSelector(
  reportingCurrencyCodeSelector,
  currencyCode => currencyCode === FUNCTIONAL_CURRENCY
);

export const companyCodeSelector = ({ shared }) => shared.companyCode;
export const isFetchingCompanyCodesSelector = ({ shared }) => shared.isFetchingCompanyCodes;
export const subledgerFlagCompanyCodeDataSelector = createSelector(
  companyCodeSelector,
  subledgerFlagCompanyCodesSelector,
  (companyCode, companyCodes) => {
    if (!companyCode) {
      return null;
    }

    return companyCodes.find(({ value }) => value === companyCode);
  }
);

export const isCompanyCodeSubledgerReadySelector = createSelector(
  subledgerFlagCompanyCodeDataSelector,
  subledgerFlagCompanyCode => !!subledgerFlagCompanyCode
);

export const functionalCurrencySelector = state => state.provisionfunctionalCurrency;

export const alertSelector = state => state.shared.alert;

export const companyCodeInContextSelector = createSelector(
  subledgerFlagCompanyCodeDataSelector,
  companyCode => {
    if (!companyCode) {
      return null;
    }

    return {
      label: companyCode.label,
      value: companyCode.value,
    };
  }
);

export const bookTypesSelector = ({ shared }) => shared.bookTypes;
export const bookTypesOptionsSelector = createSelector(
  bookTypesSelector,
  bookTypes => bookTypes.map(bookType => ({ label: bookType.tagDescription, value: bookType.tagCode }))
);
export const bookTypesFetchingSelector = ({ shared }) => shared.bookTypesFetching;
export const excelReportsSelector = ({ shared }) => shared.excelReports;

export const userFirstNameSelector = ({ user }) => user.user.firstName;
export const userLastNameSelector = ({ user }) => user.user.lastName;
export const userFullNameSelector = createSelector(
  [
    userFirstNameSelector,
    userLastNameSelector,
  ],
  (firstName, lastName) => `${firstName} ${lastName}`
);

export const userPermissionCommandsSelector = ({ user }) => user.user?.permissions?.commands;

export const countryTaxTypeDescriptionsSelector = ({ shared }) => shared.countryTaxTypeDescriptions;
export const countryTaxTypesByDescriptionSelector = createSelector(
  countryTaxTypeDescriptionsSelector,
  invert
);

export const countryTaxTypeOptionsSelector = createSelector(
  [countryTaxTypeDescriptionsSelector],
  countryTaxTypes => countryTaxTypes
    ? Object.keys(countryTaxTypes).map(key => (
      {
        label: countryTaxTypes[key],
        value: key,
      }))
    : []
);

export const isFetchingCountryTaxTypeDescriptionsSelector = ({ shared }) => shared.isFetchingCountryTaxTypeDescriptions;

export const canEditPriorPeriodDataSelector = state => state.user.user.permissions.commands.canEditPriorPeriodData;

export const featureFlagsSelector = createSelector(
  currentClientSelector,
  currentClient => currentClient?.featureFlags
);

export const bookToTaxCodeEnabledFlagSelector = createSelector(
  [featureFlagsSelector],
  featureFlags => featureFlags.bookToTaxCodeEnabled.value
);

export const allowOldTaxExpenseFlagSelector = createSelector(
  [featureFlagsSelector],
  featureFlags => featureFlags?.oldTaxExpenseFlag ? featureFlags.oldTaxExpenseFlag.value : false
);

export const generateJournalFlagSelector = createSelector(
  featureFlagsSelector,
  featureFlags => featureFlags?.generateJournalFlag ? featureFlags.generateJournalFlag.value : false
);

export const displayCRExportFlagSelector = createSelector(
  featureFlagsSelector,
  featureFlags => featureFlags?.displayCRExportFlag ? featureFlags.displayCRExportFlag.value : false
);

export const bsmFlagSelector = createSelector(
  featureFlagsSelector,
  featureFlags => featureFlags?.bsmFlag ? featureFlags.bsmFlag.value : false
);

export const fetchingPriovisionDataSelector = ({ shared }) => shared.fetchingPriovisionData;

export const categoriesSelector = ({ shared }) => shared.categories;

// Revert to getting from store, when TLS-7909 will be fixed
const categories = [
  {
    categoryName: 'DEF_GRP',
    description: 'Deferred Group',
  },
  {
    categoryName: 'ETR_GRP',
    description: 'ETR Group',
  },
];

export const categoriesStaticSelector = () => categories;

export const isLoadingCategoriesSelector = ({ shared }) => shared.isLoadingCategories;


export const showScenariosMenuSelector = createSelector(
  appDataSelector,
  featureFlagsSelector,
  (appData, featureFlags) => appData?.showScenariosMenu && featureFlags?.scenariosEnabled?.value
);

export const isInitialDataLoadedSelector = ({ shared }) => shared.isInitialDataLoaded;

export const userAllowedRoutesSelector = createSelector(
  userSelector,
  user => allRoutesArray.filter(route => {
    if (!routesPermissions[route]) {
      return true;
    }

    const { withPermissions } = routesPermissions[route];

    if (!withPermissions?.length) {
      return true;
    }

    return withPermissions.some(({ permissionPath, permissionType }) => {
      const permission = getPermissionsFromTree(user.permissions, permissionPath);
      return permission[permissionType];
    });
  })
);
